<template>
	<div>
		<div class="col-md-12">
			<div class="row">
				<div class="col-md-12">
					<h3 class="page-header">
						<i class="fa fa-book animated bounceInDown show-info"></i>
						Employee Ledger
					</h3>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<p class="text-muted">
				What the company still owes each person, and what each person still owes back.
				Two separate figures — an advance folded into a salary balance is an advance
				nobody chases.
			</p>
			<div v-if="error" class="alert alert-danger">{{ error }}</div>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-4">
					<label>Search</label>
					<input
						type="text"
						class="form-control form-cascade-control input-box-short"
						v-model="search"
						placeholder="Name or code"
					/>
				</div>
				<div class="col-md-4" style="margin-top:26px">
					<span class="text-muted">Payable</span>
					<strong style="margin-left:8px;font-size:16px">{{ money(totals.payable) }}</strong>
				</div>
				<div class="col-md-4" style="margin-top:26px">
					<span class="text-muted">Recoverable</span>
					<strong style="margin-left:8px;font-size:16px">{{ money(totals.recoverable) }}</strong>
				</div>
			</div>
		</div>

		<div class="col-md-12">
			<div class="panel">
				<div class="panel-body" style="background:none">
					<div v-if="loading" class="text-muted">Loading…</div>

					<table v-else class="table table-bordered table-hover table-striped display">
						<thead class="table-font" style="background:#475c78;color:#fff">
							<th>#</th>
							<th>Employee</th>
							<th class="text-right">Salary payable</th>
							<th class="text-right">Advance outstanding</th>
							<th class="text-center">Unpaid slips</th>
							<th></th>
						</thead>
						<tbody class="table-font">
							<tr v-for="(row, index) in filtered" :key="row.employee_id">
								<td>{{ index + 1 }}</td>
								<td>
									{{ row.name }}
									<div class="text-muted" style="font-size:11px">{{ row.employee_code || '' }}</div>
								</td>
								<td class="text-right">
									<strong :class="{ 'text-success': row.salary_balance > 0 }">
										{{ moneyShort(row.salary_balance) }}
									</strong>
								</td>
								<td class="text-right">
									<span :class="{ 'text-danger': row.advance_outstanding > 0 }">
										{{ moneyShort(row.advance_outstanding) }}
									</span>
								</td>
								<td class="text-center">
									<span v-if="row.unpaid_slips" class="badge bg-warning text-dark">
										{{ row.unpaid_slips }}
									</span>
									<span v-else class="text-muted">—</span>
								</td>
								<td>
									<button class="badge bg-primary" @click="detail(row)">Statement</button>
								</td>
							</tr>
							<tr v-if="!filtered.length">
								<td colspan="6" class="text-center text-muted" style="padding:24px">
									Nothing here yet. Approve a payroll run and the ledger fills itself.
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { payLedgerService, errorMessage } from './payrollService'
import payrollFormat from './payrollFormat'

/**
 * The employee account, per person.
 *
 * Reads pay_ledger, where the balance is computed at read time rather than
 * stored — so there is no per-insert rewrite of every later row to fall out of
 * step, and no financial-year boundary at which an undrawn balance resets.
 * Salary nobody takes simply accumulates.
 */
export default {
	name: 'PayrollEmployeeLedger',
	mixins: [payrollFormat],
	data() {
		return {
			rows: [],
			search: '',
			loading: false,
			error: '',
		}
	},
	computed: {
		filtered() {
			const term = this.search.trim().toLowerCase()
			if (!term) return this.rows
			return this.rows.filter((row) =>
				`${row.name || ''} ${row.employee_code || ''}`.toLowerCase().includes(term)
			)
		},
		totals() {
			return this.filtered.reduce(
				(sum, row) => ({
					payable: sum.payable + Number(row.salary_balance || 0),
					recoverable: sum.recoverable + Number(row.advance_outstanding || 0),
				}),
				{ payable: 0, recoverable: 0 }
			)
		},
	},
	mounted() {
		this.refresh()
	},
	methods: {
		refresh() {
			this.loading = true
			this.error = ''
			payLedgerService
				.list()
				.then((data) => {
					this.rows = data || []
				})
				.catch((e) => {
					this.error = errorMessage(e, 'Could not load the employee ledger.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		detail(row) {
			this.$router.push({ name: 'payroll-ledger-detail', params: { employeeId: row.employee_id } })
		},
	},
}
</script>

<style scoped>
.text-right {
	text-align: right;
}
.text-center {
	text-align: center;
}
</style>
